import { BaseSqliteRepository } from './base-sqlite.repository';
import { SqliteQueryError } from '../../errors';
import { ValidationError } from '@app/domain/entities';

import type {
  IExpenseRepository,
  ExpenseFilter,
  ExpenseCursor,
  ExpenseSummary,
  TransferCandidateQuery,
} from '@app/domain/ports';
import type { Expense, ExpenseListProjection } from '@app/domain/entities';

interface ExpenseRow {
  id: number;
  date: number; // Unix timestamp in seconds
  accountId: number;
  accountName: string;
  categoryId: number;
  categoryName: string;
  parentCategoryId: number;
  parentCategoryName: string | null;
  mcc: number | null;
  amount: number;
  currencyCode: string;
  rate: number;
  note: string;
  tagIdsJson?: string;
}

export class ExpenseRepository extends BaseSqliteRepository implements IExpenseRepository {
  private readonly BASE_FROM = `
    FROM expenses e
    INNER JOIN accounts a ON e.account_id = a._id
    INNER JOIN categories cat ON e.category_id = cat._id
    LEFT JOIN categories parent_cat ON cat.parent_category_id = parent_cat._id AND cat.parent_category_id != 0
    INNER JOIN currencies c ON e.currency_id = c._id
    INNER JOIN available_currencies AS cl ON c.available_currency_id = cl._id
    WHERE e.is_deleted = 0
  `;

  private readonly BASE_FIELDS = [
    'e._id as id',
    'e.expense_date as date',
    'e.account_id as accountId',
    'a.name as accountName',
    'e.category_id as categoryId',
    'cat.name as categoryName',
    'cat.parent_category_id as parentCategoryId',
    'parent_cat.name as parentCategoryName',
    'e.merchant_category_code as mcc',
    'e.amount as amount',
    'cl.ticker as currencyCode',
    'e.exchange_rate as rate',
    'e.note as note',
    "COALESCE((SELECT json_group_array(et.tag_id) FROM expense_tags et WHERE et.expense_id = e._id), '[]') as tagIdsJson",
  ];

  /**
   * Minimal FROM for aggregate queries (COUNT / SUM).
   * Avoids the 4-way JOIN used by BASE_FROM — with large tables the joins
   * cause thousands of random B-tree lookups that dominate OPFS I/O.
   * Filters that need join columns (currencyCode, categoryId) are rewritten
   * in applyFilterForAggregates() to use a subquery instead.
   */
  private readonly AGGREGATE_FROM = `FROM expenses e WHERE e.is_deleted = 0`;

  async count(filter?: ExpenseFilter): Promise<number> {
    const sql = `SELECT COUNT(*) AS total ${this.AGGREGATE_FROM}`;
    const { query, params } = this.applyFilterForAggregates(sql, filter);
    const result = await this.queryOne<{ total: number }>(query, params);
    return result?.total ?? 0;
  }

  async aggregateTotalAmount(filter: ExpenseFilter): Promise<number> {
    const sql = `SELECT SUM(e.amount) AS total ${this.AGGREGATE_FROM}`;
    const { query, params } = this.applyFilterForAggregates(sql, filter);
    const result = await this.queryOne<{ total: number }>(query, params);
    return result?.total ?? 0;
  }

  async getSummary(filter?: ExpenseFilter): Promise<ExpenseSummary> {
    const sql = `SELECT COUNT(*) AS total, COALESCE(SUM(e.amount), 0) AS totalAmount ${this.AGGREGATE_FROM}`;
    const { query, params } = this.applyFilterForAggregates(sql, filter);
    const result = await this.queryOne<ExpenseSummary>(query, params);

    return {
      total: result?.total ?? 0,
      totalAmount: result?.totalAmount ?? 0,
    };
  }

  async findAll(cursor: ExpenseCursor, filter?: ExpenseFilter): Promise<ExpenseListProjection[]> {
    const t0 = performance.now();
    const pageSql = `
      SELECT
        e._id as id,
        e.expense_date as date,
        e.account_id as accountId,
        e.category_id as categoryId,
        e.currency_id as currencyId,
        e.merchant_category_code as mcc,
        e.amount as amount,
        e.exchange_rate as rate,
        e.note as note
      ${this.AGGREGATE_FROM}
    `;

    // eslint-disable-next-line prefer-const
    let { query: pageQuery, params } = this.applyFilterForAggregates(pageSql, filter);

    pageQuery += ` ORDER BY e.expense_date DESC, e._id DESC`;
    if (cursor.limit || cursor.offset) {
      pageQuery += ` LIMIT ? OFFSET ?`;
      params.push(cursor.limit ?? -1, cursor.offset ?? 0);
    }

    const query = `
      WITH page AS (
        ${pageQuery}
      )
      SELECT
        p.id as id,
        p.date as date,
        p.accountId as accountId,
        a.name as accountName,
        p.categoryId as categoryId,
        cat.name as categoryName,
        cat.parent_category_id as parentCategoryId,
        parent_cat.name as parentCategoryName,
        p.mcc as mcc,
        p.amount as amount,
        ${filter?.currencyCode ? '?' : 'cl.ticker'} as currencyCode,
        p.rate as rate,
        p.note as note,
        COALESCE((SELECT json_group_array(et.tag_id) FROM expense_tags et WHERE et.expense_id = p.id), '[]') as tagIdsJson
      FROM page p
      INNER JOIN accounts a ON p.accountId = a._id
      INNER JOIN categories cat ON p.categoryId = cat._id
      LEFT JOIN categories parent_cat ON cat.parent_category_id = parent_cat._id AND cat.parent_category_id != 0
      ${filter?.currencyCode ? '' : 'INNER JOIN currencies c ON p.currencyId = c._id INNER JOIN available_currencies cl ON c.available_currency_id = cl._id'}
      ORDER BY p.date DESC, p.id DESC
    `;

    if (filter?.currencyCode) {
      params.push(filter.currencyCode);
    }

    const tBeforeListQuery = performance.now();
    const rows = await this.query<ExpenseRow>(query, params);
    const listQueryMs = performance.now() - tBeforeListQuery;
    if (rows.length === 0) {
      console.debug(
        `[trace:ExpenseRepository] findAll total=${(performance.now() - t0).toFixed(2)}ms — listQuery=${listQueryMs.toFixed(2)}ms, rows=0`,
      );
      return [];
    }

    const tBeforeMapping = performance.now();
    const result = rows.map((row) => {
      return {
        id: row.id,
        date: new Date(row.date * 1000),
        accountId: row.accountId,
        accountName: row.accountName,
        categoryId: row.categoryId,
        categoryName: row.categoryName,
        parentCategoryId: row.parentCategoryId || undefined,
        parentCategoryName: row.parentCategoryName || undefined,
        ...(row.mcc != null ? { mcc: row.mcc } : {}),
        amount: row.amount,
        currencyCode: row.currencyCode,
        rate: !row.rate ? 1 : row.rate,
        note: row.note,
        tagIds: this.parseTagIds(row.tagIdsJson),
      };
    });

    console.debug(
      `[trace:ExpenseRepository] findAll total=${(performance.now() - t0).toFixed(2)}ms — listQuery=${listQueryMs.toFixed(2)}ms, mapping=${(performance.now() - tBeforeMapping).toFixed(2)}ms, rows=${rows.length}`,
    );

    return result;
  }

  async getById(id: number): Promise<Expense | null> {
    const query = `
      SELECT
      ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
       AND e._id = ?`;
    const row = await this.queryOne<ExpenseRow>(query, [id]);
    return row ? this.mapRowToEntity(row) : null;
  }

  async getByIdIncludingDeleted(id: number): Promise<Expense | null> {
    const query = `
      SELECT
      ${this.BASE_FIELDS.join(',\n ')}
      FROM expenses e
      INNER JOIN accounts a ON e.account_id = a._id
      INNER JOIN categories cat ON e.category_id = cat._id
      LEFT JOIN categories parent_cat ON cat.parent_category_id = parent_cat._id AND cat.parent_category_id != 0
      INNER JOIN currencies c ON e.currency_id = c._id
      INNER JOIN available_currencies AS cl ON c.available_currency_id = cl._id
      WHERE e._id = ?`;
    const row = await this.queryOne<ExpenseRow>(query, [id]);
    return row ? this.mapRowToEntity(row) : null;
  }

  async softDeleteKeepingMetadata(id: number): Promise<void> {
    await this.execute(
      "UPDATE expenses SET is_deleted = (SELECT max(is_deleted) + 1 FROM expenses), updated_at = strftime('%s','now') WHERE _id = ?",
      [id],
    );
  }

  async restore(id: number): Promise<void> {
    await this.execute("UPDATE expenses SET is_deleted = 0, updated_at = strftime('%s','now') WHERE _id = ?", [id]);
  }

  async findTransferCandidates(query: TransferCandidateQuery): Promise<Expense[]> {
    const dateFrom = new Date(query.dateFrom).setUTCHours(0, 0, 0, 0) / 1000;
    const dateTo = new Date(query.dateTo).setUTCHours(0, 0, 0, 0) / 1000;
    const anchor = new Date(query.anchorDate).setUTCHours(0, 0, 0, 0) / 1000;

    const sql = `
      SELECT
      ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
        AND e.amount = ?
        AND cl.ticker = ?
        AND e.account_id <> ?
        AND e.expense_date BETWEEN ? AND ?
        AND NOT EXISTS (
          SELECT 1 FROM account_transfers t
          WHERE t.source_expense_id = e._id AND t.is_deleted = 0
        )
      ORDER BY ABS(e.expense_date - ?) ASC, e.expense_date DESC, e._id DESC`;

    const rows = await this.query<ExpenseRow>(sql, [
      query.amount,
      query.currencyCode,
      query.excludeAccountId,
      dateFrom,
      dateTo,
      anchor,
    ]);
    return rows.map((row) => this.mapRowToEntity(row));
  }

  async insert(
    expense: Omit<Expense, 'id' | 'accountName' | 'categoryName' | 'unitName'> & { tagIds?: number[] },
  ): Promise<number> {
    const date = new Date(expense.date).setUTCHours(0, 0, 0, 0) / 1000;

    const t0 = performance.now();
    const result = await this.queryOne<{ id: number }>(
      `INSERT INTO expenses (expense_date, account_id, category_id, merchant_category_code, amount, currency_id, exchange_rate, note, note_normalized)
       VALUES (?, ?, ?, ?, ?, (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?), ?, ?, ?)
       RETURNING _id AS id`,
      [
        date,
        expense.accountId,
        expense.categoryId,
        expense.mcc ?? null,
        expense.amount,
        expense.currencyCode,
        expense.rate,
        expense.note,
        expense.note?.toLowerCase() ?? '',
      ],
    );
    console.debug(`[trace:ExpenseRepository] insert (SQL + triggers): ${(performance.now() - t0).toFixed(2)}ms`);

    if (!result?.id) {
      throw new SqliteQueryError('INSERT INTO expenses', [], 'Failed to insert expense');
    }

    await this.replaceExpenseTags(result.id, expense.tagIds);

    return result.id;
  }

  async update(
    expense: Omit<Expense, 'accountName' | 'categoryName' | 'unitName'> & { tagIds?: number[] },
  ): Promise<void> {
    const date = new Date(expense.date).setUTCHours(0, 0, 0, 0) / 1000;

    const t0 = performance.now();
    await this.execute(
      `UPDATE expenses
       SET expense_date = ?,
           account_id = ?,
           category_id = ?,
           merchant_category_code = ?,
           amount = ?,
           currency_id = (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?),
           exchange_rate = ?,
           note = ?,
           note_normalized = ?,
           updated_at = strftime('%s','now')
       WHERE _id = ?`,
      [
        date,
        expense.accountId,
        expense.categoryId,
        expense.mcc ?? null,
        expense.amount,
        expense.currencyCode,
        expense.rate,
        expense.note,
        expense.note?.toLowerCase() ?? '',
        expense.id,
      ],
    );

    if (expense.tagIds !== undefined) {
      await this.replaceExpenseTags(expense.id, expense.tagIds);
    }

    console.debug(`[trace:ExpenseRepository] update (SQL + triggers): ${(performance.now() - t0).toFixed(2)}ms`);
  }

  async delete(id: number): Promise<void> {
    await this.execute(`DELETE FROM expense_tags WHERE expense_id = ?`, [id]);
    await this.execute(
      "UPDATE expenses SET is_deleted = (SELECT max(is_deleted) + 1 FROM expenses), updated_at = strftime('%s','now') WHERE _id = ?",
      [id],
    );
  }

  async bulkDelete(survivorId: number, idsToDelete: number[]): Promise<void> {
    if (idsToDelete.length === 0) return;

    const placeholders = idsToDelete.map(() => '?').join(', ');

    // Re-point external transaction links to the survivor before soft-deleting
    await this.execute(
      `UPDATE external_transactions SET expense_id = ?
       WHERE expense_id IN (${placeholders}) AND is_deleted = 0`,
      [survivorId, ...idsToDelete],
    );

    // Soft-delete the non-survivor expenses
    await this.execute(
      `UPDATE expenses SET is_deleted = (SELECT max(is_deleted) + 1 FROM expenses), updated_at = strftime('%s','now')
       WHERE _id IN (${placeholders})`,
      idsToDelete,
    );

    await this.execute(`DELETE FROM expense_tags WHERE expense_id IN (${placeholders})`, idsToDelete);
  }

  async countByCategory(categoryId: number): Promise<number> {
    const query = `
      SELECT COUNT(*) AS total
      FROM expenses e
      WHERE e.category_id = ? AND e.is_deleted = 0
    `;
    const result = await this.queryOne<{ total: number }>(query, [categoryId]);
    return result?.total ?? 0;
  }

  async bulkUpdateCategory(sourceCategoryId: number, targetCategoryId: number): Promise<void> {
    await this.execute(
      `UPDATE expenses
       SET category_id = ?,
           updated_at = strftime('%s','now')
       WHERE category_id = ? AND is_deleted = 0`,
      [targetCategoryId, sourceCategoryId],
    );
  }

  async bulkSetCategory(ids: number[], categoryId: number): Promise<void> {
    if (ids.length === 0) return;
    const placeholders = ids.map(() => '?').join(', ');
    await this.execute(
      `UPDATE expenses
       SET category_id = ?,
           updated_at = strftime('%s','now')
       WHERE _id IN (${placeholders}) AND is_deleted = 0`,
      [categoryId, ...ids],
    );
  }

  async bulkAddTags(expenseIds: number[], tagIds: number[]): Promise<void> {
    if (expenseIds.length === 0) return;

    const normalizedTagIds = Array.from(new Set(tagIds)).filter((id) => Number.isInteger(id) && id > 0);
    if (normalizedTagIds.length === 0) {
      throw new ValidationError('tagIds', 'At least one valid tag ID is required');
    }

    const normalizedExpenseIds = Array.from(new Set(expenseIds)).filter((id) => Number.isInteger(id) && id > 0);
    if (normalizedExpenseIds.length === 0) return;

    const pairs = normalizedExpenseIds.flatMap((expenseId) =>
      normalizedTagIds.map((tagId) => [expenseId, tagId] as const),
    );
    const chunkSize = 400;

    for (let offset = 0; offset < pairs.length; offset += chunkSize) {
      const chunk = pairs.slice(offset, offset + chunkSize);
      const values = chunk.map(() => '(?, ?)').join(', ');
      const params = chunk.flatMap(([expenseId, tagId]) => [expenseId, tagId]);
      await this.execute(
        `WITH pairs(expense_id, tag_id) AS (VALUES ${values})
         INSERT OR IGNORE INTO expense_tags (expense_id, tag_id)
         SELECT pairs.expense_id, pairs.tag_id
         FROM pairs
         INNER JOIN expenses e ON e._id = pairs.expense_id AND e.is_deleted = 0
         INNER JOIN tags t ON t._id = pairs.tag_id`,
        params,
      );
    }

    for (let offset = 0; offset < normalizedExpenseIds.length; offset += chunkSize) {
      const chunk = normalizedExpenseIds.slice(offset, offset + chunkSize);
      const placeholders = chunk.map(() => '?').join(', ');
      await this.execute(
        `UPDATE expenses
         SET updated_at = strftime('%s','now')
         WHERE _id IN (${placeholders}) AND is_deleted = 0`,
        chunk,
      );
    }
  }

  async countByAccount(accountId: number): Promise<number> {
    const query = `
      SELECT COUNT(*) AS total
      FROM expenses e
      WHERE e.account_id = ? AND e.is_deleted = 0
    `;
    const result = await this.queryOne<{ total: number }>(query, [accountId]);
    return result?.total ?? 0;
  }

  async bulkUpdateAccount(sourceAccountId: number, targetAccountId: number): Promise<void> {
    await this.execute(
      `UPDATE expenses
       SET account_id = ?,
           updated_at = strftime('%s','now')
       WHERE account_id = ? AND is_deleted = 0`,
      [targetAccountId, sourceAccountId],
    );
  }

  async findRecentCategoryIdsByNote(params: { note: string; limit: number }): Promise<number[]> {
    const normalizedNote = params.note.trim().toLowerCase();
    if (!normalizedNote || params.limit <= 0) {
      return [];
    }

    const rows = await this.query<{ categoryId: number }>(
      `SELECT e.category_id as categoryId
       FROM expenses e
       WHERE e.is_deleted = 0
         AND e.note_normalized = ?
       ORDER BY e.expense_date DESC, e._id DESC
       LIMIT ?`,
      [normalizedNote, params.limit],
    );

    return rows.map((row) => row.categoryId);
  }

  async findSimilarTransaction(params: {
    date: Date;
    accountId: number;
    amount: number;
    currencyCode: string;
    amountTolerance: number;
    externalSystem: string;
  }): Promise<number | null> {
    const normalizedDate = new Date(params.date).setUTCHours(0, 0, 0, 0) / 1000;
    const minAmount = params.amount - params.amountTolerance;
    const maxAmount = params.amount + params.amountTolerance;

    const query = `
      SELECT e._id as id
      FROM expenses e
      INNER JOIN accounts a ON e.account_id = a._id
      INNER JOIN currencies c ON e.currency_id = c._id
      INNER JOIN available_currencies cl ON c.available_currency_id = cl._id
      WHERE e.is_deleted = 0
        AND e.account_id = ?
        AND cl.ticker = ?
        AND e.expense_date = ?
        AND e.amount >= ?
        AND e.amount <= ?
        AND NOT EXISTS (
          SELECT 1 FROM external_transactions et
          WHERE et.expense_id = e._id
            AND et.source = ?
            AND et.is_deleted = 0
        )
      ORDER BY ABS(e.amount - ?) ASC
      LIMIT 1
    `;

    const row = await this.queryOne<{ id: number }>(query, [
      params.accountId,
      params.currencyCode,
      normalizedDate,
      minAmount,
      maxAmount,
      params.externalSystem,
      params.amount,
    ]);

    return row?.id ?? null;
  }

  async findCandidatesForImport(params: {
    externalSystem: string;
    accountIds: number[];
    dateFrom: Date;
    dateTo: Date;
  }): Promise<Array<{ id: number; date: number; accountId: number; currencyCode: string; amount: number }>> {
    if (params.accountIds.length === 0) {
      return [];
    }

    const dateFrom = new Date(params.dateFrom).setUTCHours(0, 0, 0, 0) / 1000;
    const dateTo = new Date(params.dateTo).setUTCHours(0, 0, 0, 0) / 1000;
    const placeholders = params.accountIds.map(() => '?').join(', ');

    const query = `
      SELECT e._id as id,
             e.expense_date as date,
             e.account_id as accountId,
             cl.ticker as currencyCode,
             e.amount as amount
      FROM expenses e
      INNER JOIN currencies c ON e.currency_id = c._id
      INNER JOIN available_currencies cl ON c.available_currency_id = cl._id
      WHERE e.is_deleted = 0
        AND e.account_id IN (${placeholders})
        AND e.expense_date >= ?
        AND e.expense_date <= ?
        AND NOT EXISTS (
          SELECT 1 FROM external_transactions et
          WHERE et.expense_id = e._id
            AND et.source = ?
            AND et.is_deleted = 0
        )
    `;

    return await this.query<{ id: number; date: number; accountId: number; currencyCode: string; amount: number }>(
      query,
      [...params.accountIds, dateFrom, dateTo, params.externalSystem],
    );
  }

  /**
   * Like applyFilter but for aggregate-only queries (COUNT / SUM) that do not
   * join accounts / categories / currencies.
   *
   * - categoryId  → uses `e.category_id` directly (no `cat` alias needed)
   * - currencyCode → resolved via a scalar subquery instead of a JOIN
   * - all other filter fields reference only the `expenses e` table
   */
  private applyFilterForAggregates(
    query: string,
    filter?: ExpenseFilter,
    params: unknown[] = [],
  ): { query: string; params: unknown[] } {
    if (filter?.accountId) {
      query += ` AND e.account_id = ?`;
      params.push(filter.accountId);
    }

    if (filter?.categoryId) {
      query += ` AND e.category_id IN (
        SELECT ? AS cat_id
        UNION ALL
        SELECT _id FROM categories WHERE parent_category_id = ? AND is_deleted = 0
      )`;
      params.push(filter.categoryId);
      params.push(filter.categoryId);
    }

    const toValidCategoryIds = (ids: number[] | undefined): number[] =>
      Array.from(new Set(ids ?? [])).filter((id) => Number.isInteger(id) && id > 0);

    const includeCategoryIds = toValidCategoryIds(filter?.includeCategoryIds);
    if (includeCategoryIds.length > 0) {
      const placeholders = includeCategoryIds.map(() => '?').join(', ');
      query += ` AND (
        e.category_id IN (${placeholders})
        OR e.category_id IN (SELECT _id FROM categories WHERE parent_category_id IN (${placeholders}) AND is_deleted = 0)
      )`;
      params.push(...includeCategoryIds, ...includeCategoryIds);
    }

    const excludeCategoryIds = toValidCategoryIds(filter?.excludeCategoryIds);
    if (excludeCategoryIds.length > 0) {
      const placeholders = excludeCategoryIds.map(() => '?').join(', ');
      query += ` AND NOT (
        e.category_id IN (${placeholders})
        OR e.category_id IN (SELECT _id FROM categories WHERE parent_category_id IN (${placeholders}) AND is_deleted = 0)
      )`;
      params.push(...excludeCategoryIds, ...excludeCategoryIds);
    }

    if (filter?.currencyCode) {
      query += ` AND e.currency_id = (
        SELECT c._id FROM currencies c
        INNER JOIN available_currencies cl ON c.available_currency_id = cl._id
        WHERE cl.ticker = ?
      )`;
      params.push(filter.currencyCode);
    }

    if (filter?.dateFrom) {
      query += ` AND e.expense_date >= ?`;
      params.push(new Date(filter.dateFrom).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.dateTo) {
      query += ` AND e.expense_date < ?`;
      params.push(new Date(filter.dateTo).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.amountFrom) {
      query += ` AND e.amount >= ?`;
      params.push(filter.amountFrom);
    }

    if (filter?.amountTo) {
      query += ` AND e.amount <= ?`;
      params.push(filter.amountTo);
    }

    if (filter?.note) {
      query += ` AND e.note_normalized LIKE ?`;
      params.push(`%${filter.note.toLowerCase()}%`);
    }

    const toValidTagIds = (ids: number[] | undefined): number[] =>
      Array.from(new Set(ids ?? [])).filter((id) => Number.isInteger(id) && id > 0);

    // Merge legacy tagIds into includeTagIds for backward compatibility
    const includeTagIds = toValidTagIds([...(filter?.includeTagIds ?? []), ...(filter?.tagIds ?? [])]);

    for (const tagId of includeTagIds) {
      query += ` AND EXISTS (SELECT 1 FROM expense_tags et WHERE et.expense_id = e._id AND et.tag_id = ?)`;
      params.push(tagId);
    }

    const excludeTagIds = toValidTagIds(filter?.excludeTagIds);

    for (const tagId of excludeTagIds) {
      query += ` AND NOT EXISTS (SELECT 1 FROM expense_tags et WHERE et.expense_id = e._id AND et.tag_id = ?)`;
      params.push(tagId);
    }

    return { query, params };
  }

  private async replaceExpenseTags(expenseId: number, tagIds?: number[]): Promise<void> {
    if (tagIds === undefined) {
      return;
    }

    const normalizedTagIds = Array.from(new Set(tagIds)).filter((id) => Number.isInteger(id) && id > 0);
    await this.execute(`DELETE FROM expense_tags WHERE expense_id = ?`, [expenseId]);

    if (normalizedTagIds.length === 0) {
      return;
    }

    const valuesClause = normalizedTagIds.map(() => '(?, ?)').join(', ');
    const params: number[] = [];
    for (const tagId of normalizedTagIds) {
      params.push(expenseId, tagId);
    }

    await this.execute(`INSERT INTO expense_tags (expense_id, tag_id) VALUES ${valuesClause}`, params);
  }

  private parseTagIds(tagIdsJson?: string): number[] {
    if (!tagIdsJson) {
      return [];
    }

    try {
      const parsed = JSON.parse(tagIdsJson);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.filter((id): id is number => Number.isInteger(id) && id > 0);
    } catch (error) {
      console.warn('Failed to parse tag IDs JSON', error);
      return [];
    }
  }

  private mapRowToEntity<T extends ExpenseRow, P extends Expense>(row: T, configure?: (row: T, model: P) => void): P {
    const model = {
      id: row.id,
      date: new Date(row.date * 1000),
      accountId: row.accountId,
      accountName: row.accountName,
      categoryId: row.categoryId,
      categoryName: row.categoryName,
      mcc: row.mcc ?? undefined,
      amount: row.amount,
      currencyCode: row.currencyCode,
      rate: !row.rate ? 1 : row.rate,
      note: row.note,
      tagIds: this.parseTagIds(row.tagIdsJson),
    } as P;

    configure?.(row, model);
    return model;
  }

  /**
   * Get MCC frequency by category (minimum 3 occurrences)
   * Used for suggesting MCC-to-category mappings
   */
  async getMccFrequency(): Promise<Array<{ categoryId: number; categoryName: string; mcc: number; count: number }>> {
    const results = await this.query<{ categoryId: number; categoryName: string; mcc: number; count: number }>(
      `SELECT
        e.category_id as categoryId,
        cat.name as categoryName,
        e.merchant_category_code as mcc,
        COUNT(*) as count
      FROM expenses e
      INNER JOIN categories cat ON e.category_id = cat._id
      WHERE e.is_deleted = 0
        AND e.merchant_category_code IS NOT NULL
        AND cat.is_deleted = 0
      GROUP BY e.category_id, e.merchant_category_code
      HAVING count >= 3
      ORDER BY count DESC`,
    );

    return results;
  }
}
